// skipcq: JS-W1028
import { useCallback, useContext } from 'react';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StoreIcon from '@mui/icons-material/Store';
import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import { BrewerySessionContext, defaultBrewerySession } from '../../context/BrewerySessionContext';
import { parseBrewhouseScopeValue } from '../../utils/brewhouse/brewhouseActiveScope';
import { trackBrewhouseSwitchActiveLocation } from '../../utils/brewhousePortalAnalytics';
import { PRIMARY_DETAIL_TEXT } from '../../utils/theme';

function ScopeOptionIcon({ kind }) {
  const IconComponent = kind === 'group' ? AccountTreeIcon : StoreIcon;
  return (
    <ListItemIcon sx={{ minWidth: 28 }}>
      <IconComponent sx={{ fontSize: 18, color: 'text.secondary' }} aria-hidden />
    </ListItemIcon>
  );
}

ScopeOptionIcon.propTypes = {
  kind: PropTypes.oneOf(['group', 'location']).isRequired,
};

function renderScopeSelectorLabel(label) {
  return (
    <Typography
      variant="subtitle1"
      component="span"
      sx={{
        fontFamily: 'Lora, Georgia, serif',
        fontWeight: 600,
        color: PRIMARY_DETAIL_TEXT,
      }}
    >
      {label}
    </Typography>
  );
}

function BrewhouseActiveLocationBar() {
  const session = useContext(BrewerySessionContext) ?? defaultBrewerySession;

  const handleScopeChange = useCallback(
    (event) => {
      const nextScopeValue = event.target.value;
      const previousScopeValue = session.activeScopeValue ?? '';
      if (!nextScopeValue || nextScopeValue === previousScopeValue) {
        return;
      }

      session.setActiveScopeValue(nextScopeValue);

      const parsedScope = parseBrewhouseScopeValue(nextScopeValue);
      const previousScope = parseBrewhouseScopeValue(previousScopeValue);
      const nextBreweryId =
        parsedScope?.kind === 'location'
          ? parsedScope.id
          : session.breweryId ?? '';
      const previousBreweryId =
        previousScope?.kind === 'location'
          ? previousScope.id
          : session.breweryId ?? '';

      try {
        trackBrewhouseSwitchActiveLocation({
          breweryId: nextBreweryId,
          previousBreweryId,
          locationCount: session.breweries.length,
        });
      } catch {
        // Location switch must succeed even when analytics capture fails.
      }
    },
    [session],
  );

  const selectorLabel =
    session.activeScopeLabel || session.activeLocationLabel || session.activeBreweryName;

  const renderSelectedScope = useCallback(
    () => renderScopeSelectorLabel(selectorLabel),
    [selectorLabel],
  );

  if (session.isLoading || session.breweryId === null) {
    return null;
  }

  return (
    <Box
      data-testid="brewhouse-active-location-bar"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', sm: 'center' },
        py: 0.75,
        borderTop: 1,
        borderColor: 'divider',
        mt: 0.75,
      }}
    >
      {session.showLocationSelector ? (
        <Select
          value={session.activeScopeValue}
          onChange={handleScopeChange}
          variant="standard"
          disableUnderline
          displayEmpty
          IconComponent={KeyboardArrowDownIcon}
          inputProps={{
            'aria-label': 'Active brewery location',
          }}
          renderValue={renderSelectedScope}
          sx={{
            minWidth: 0,
            maxWidth: '100%',
            color: PRIMARY_DETAIL_TEXT,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              py: 0,
              pr: '28px !important',
            },
            '& .MuiSelect-icon': {
              color: 'text.secondary',
              right: 0,
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: { maxHeight: 360 },
            },
          }}
        >
          {session.locationSelectorOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{ pl: 2 + option.indent * 2 }}
            >
              <ScopeOptionIcon kind={option.kind} />
              {option.label}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Typography
          variant="subtitle1"
          component="p"
          sx={{
            fontFamily: 'Lora, Georgia, serif',
            fontWeight: 600,
            color: PRIMARY_DETAIL_TEXT,
            minWidth: 0,
          }}
        >
          {selectorLabel}
        </Typography>
      )}
    </Box>
  );
}

export default BrewhouseActiveLocationBar;
